// src/expensesentry.js
import React, { useRef, useState } from 'react';

const ExpensesEntry = ({
  styles,
  isMobile,
  form,
  setField,
  parents,
  categoriesForSelectedParent,
  openAddParent,
  openAddCategory,
  openManage,
  saveExpense,
  resetForm,
  onPickFile,
  removeAttachment,
  // ✅ NEW: vendor autocomplete props
  vendors,
  showQuickAddVendor,
  setShowQuickAddVendor,
  quickVendorForm,
  setQuickVendorForm,
  quickAddVendor
}) => {
  const cameraRef = useRef(null);
  const fileAnyRef = useRef(null);
  const [vendorDropdownOpen, setVendorDropdownOpen] = useState(false);

  const onFileChange = (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (f) onPickFile(f);
  };

  const formatPhone = (value) => {
    const cleaned = String(value || '').replace(/\D/g, '');
    if (cleaned.length <= 3) return cleaned;
    if (cleaned.length <= 6) return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
  };

  // Filter vendors by what's typed
  const vendorSuggestions = (() => {
    const q = String(form.vendor || '').trim().toLowerCase();
    if (!q) return [];
    return (vendors || []).filter((v) =>
      String(v.name || '').toLowerCase().includes(q)
    ).slice(0, 8);
  })();

  const modalInputStyle = {
    width: '100%',
    padding: '12px',
    marginBottom: '10px',
    border: '3px solid black',
    fontWeight: 900,
    boxSizing: 'border-box',
    fontSize: '15px'
  };

  const modalTextareaStyle = {
    ...modalInputStyle,
    minHeight: '110px',
    fontFamily: 'inherit',
    resize: 'vertical'
  };

  return (
    <>
      {/* TOP ROW: DATE + AMOUNT + VENDOR */}
      <div style={styles.topRow}>
        <div>
          <label style={styles.label}>DATE</label>
          <input
            type="date"
            value={String(form.date || '').slice(0, 10)}
            onChange={(e) => setField('date', e.target.value)}
            style={styles.input}
          />
        </div>

        <div>
          <label style={styles.label}>AMOUNT ($)</label>
          <input
            value={form.amount}
            onChange={(e) => setField('amount', e.target.value)}
            style={styles.input}
            placeholder="0.00"
            inputMode="decimal"
          />
        </div>

        {/* ✅ VENDOR with autocomplete */}
        <div style={{ position: 'relative' }}>
          <label style={styles.label}>VENDOR</label>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
            <div style={{ flex: 1, position: 'relative' }}>
              <input
                value={form.vendor}
                onChange={(e) => {
                  setField('vendor', e.target.value);
                  setVendorDropdownOpen(true);
                }}
                onFocus={() => setVendorDropdownOpen(true)}
                onBlur={() => setTimeout(() => setVendorDropdownOpen(false), 180)}
                style={styles.input}
                placeholder="E.G. HOME DEPOT"
                autoComplete="off"
              />

              {/* Dropdown suggestions */}
              {vendorDropdownOpen && vendorSuggestions.length > 0 && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  background: 'white',
                  border: '4px solid black',
                  zIndex: 999,
                  maxHeight: '220px',
                  overflowY: 'auto'
                }}>
                  {vendorSuggestions.map((v) => (
                    <div
                      key={v.id}
                      onMouseDown={() => {
                        setField('vendor', v.name);
                        setVendorDropdownOpen(false);
                      }}
                      style={{
                        padding: '12px 14px',
                        fontWeight: 900,
                        cursor: 'pointer',
                        borderBottom: '2px solid black'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = 'yellow'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                    >
                      {v.name}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* ✅ QUICK ADD VENDOR button */}
            <button
              className="jfn-exp-btn"
              style={{
                ...styles.btnLight,
                whiteSpace: 'nowrap',
                padding: '14px 10px',
                fontSize: '13px'
              }}
              type="button"
              onClick={() => {
                setQuickVendorForm({
                  name: String(form.vendor || '').trim(),
                  contactName: '',
                  email: '',
                  phone: '',
                  address: '',
                  city: '',
                  state: '',
                  zip: '',
                  notes: ''
                });
                setShowQuickAddVendor(true);
              }}
            >
              + VENDOR
            </button>
          </div>
        </div>
      </div>

      <div style={{ height: '12px' }} />

      {/* NEXT ROW: PARENT + NOTES */}
      <div style={styles.row2}>
        <div>
          <label style={styles.label}>PARENT GROUP</label>
          <select
            value={form.parentId}
            onChange={(e) => {
              setField('parentId', e.target.value);
              setField('categoryId', '');
            }}
            style={styles.input}
          >
            <option value="">SELECT PARENT</option>
            {(parents || []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>

          <div style={{ height: '10px' }} />

          <label style={styles.label}>CATEGORY</label>
          <select
            value={form.categoryId}
            onChange={(e) => setField('categoryId', e.target.value)}
            style={{
              ...styles.input,
              opacity: form.parentId ? 1 : 0.65
            }}
            disabled={!form.parentId}
          >
            <option value="">
              {form.parentId ? 'SELECT CATEGORY' : 'SELECT PARENT FIRST'}
            </option>
            {(categoriesForSelectedParent || []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>

          <div style={{ height: '10px' }} />

          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <button className="jfn-exp-btn" style={styles.btnLight} onClick={openAddParent}>
              + ADD PARENT
            </button>
            <button className="jfn-exp-btn" style={styles.btnLight} onClick={openAddCategory}>
              + ADD CATEGORY
            </button>
            <button className="jfn-exp-btn" style={styles.btnLight} onClick={openManage}>
              MANAGE
            </button>
          </div>
        </div>

        <div>
          <label style={styles.label}>NOTES</label>
          <textarea
            value={form.notes}
            onChange={(e) => setField('notes', e.target.value)}
            style={{ ...styles.input, minHeight: isMobile ? '110px' : '140px', resize: 'vertical' }}
            placeholder="OPTIONAL NOTES"
          />

          <div style={{ height: '12px' }} />

          <label style={styles.label}>ATTACHMENT (OPTIONAL)</label>

          <input
            ref={cameraRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={onFileChange}
            style={{ display: 'none' }}
          />

          <input
            ref={fileAnyRef}
            type="file"
            accept="image/*,.pdf,.doc,.docx"
            onChange={onFileChange}
            style={{ display: 'none' }}
          />

          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
            <button
              className="jfn-exp-btn"
              style={styles.btnLight}
              onClick={() => cameraRef.current?.click()}
              type="button"
            >
              TAKE PHOTO
            </button>

            <button
              className="jfn-exp-btn"
              style={styles.btnLight}
              onClick={() => fileAnyRef.current?.click()}
              type="button"
            >
              ATTACH FILE
            </button>

            {form.attachment?.name ? (
              <>
                <div style={{ fontWeight: 900, wordBreak: 'break-word' }}>
                  {form.attachment.name}
                </div>
                <button className="jfn-exp-btn" style={styles.btnDanger} onClick={removeAttachment} type="button">
                  REMOVE
                </button>
              </>
            ) : null}
          </div>

          <div style={styles.btnRow}>
            <button className="jfn-exp-btn" style={styles.btnBlack} onClick={saveExpense} type="button">
              SAVE EXPENSE
            </button>
            <button className="jfn-exp-btn" style={styles.btnLight} onClick={resetForm} type="button">
              CLEAR
            </button>
          </div>
        </div>
      </div>

      {/* ✅ QUICK ADD VENDOR MODAL — matches VendorsSettings exactly */}
      {showQuickAddVendor && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.85)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '18px',
            zIndex: 5000
          }}
          onClick={() => setShowQuickAddVendor(false)}
        >
          <div
            style={{
              background: 'white',
              border: '8px solid black',
              padding: '20px',
              width: '100%',
              maxWidth: '650px',
              maxHeight: '90vh',
              overflowY: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ fontWeight: 900, textTransform: 'uppercase', fontSize: '20px', marginBottom: '14px' }}>
              ADD VENDOR
            </div>

            <input
              style={modalInputStyle}
              placeholder="VENDOR NAME *"
              value={quickVendorForm.name}
              onChange={(e) => setQuickVendorForm({ ...quickVendorForm, name: e.target.value })}
            />

            <input
              style={modalInputStyle}
              placeholder="CONTACT NAME"
              value={quickVendorForm.contactName}
              onChange={(e) => setQuickVendorForm({ ...quickVendorForm, contactName: e.target.value })}
            />

            <input
              style={modalInputStyle}
              placeholder="EMAIL"
              value={quickVendorForm.email}
              onChange={(e) => setQuickVendorForm({ ...quickVendorForm, email: e.target.value })}
            />

            <input
              style={modalInputStyle}
              placeholder="PHONE"
              value={quickVendorForm.phone}
              onChange={(e) => setQuickVendorForm({ ...quickVendorForm, phone: formatPhone(e.target.value) })}
            />

            <input
              style={modalInputStyle}
              placeholder="ADDRESS"
              value={quickVendorForm.address}
              onChange={(e) => setQuickVendorForm({ ...quickVendorForm, address: e.target.value })}
            />

            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: '10px' }}>
              <input
                style={modalInputStyle}
                placeholder="CITY"
                value={quickVendorForm.city}
                onChange={(e) => setQuickVendorForm({ ...quickVendorForm, city: e.target.value })}
              />
              <input
                style={modalInputStyle}
                placeholder="STATE"
                value={quickVendorForm.state}
                onChange={(e) => setQuickVendorForm({ ...quickVendorForm, state: e.target.value })}
              />
              <input
                style={modalInputStyle}
                placeholder="ZIP"
                value={quickVendorForm.zip}
                onChange={(e) => setQuickVendorForm({ ...quickVendorForm, zip: e.target.value })}
              />
            </div>

            <textarea
              style={modalTextareaStyle}
              placeholder="NOTES"
              value={quickVendorForm.notes}
              onChange={(e) => setQuickVendorForm({ ...quickVendorForm, notes: e.target.value })}
            />

            <div style={{ display: 'flex', gap: '10px', marginTop: '10px', flexWrap: 'wrap' }}>
              <button
                className="jfn-exp-btn"
                style={{ ...styles.btnBlack, flex: 1, minWidth: '160px' }}
                type="button"
                onClick={quickAddVendor}
              >
                SAVE
              </button>
              <button
                className="jfn-exp-btn"
                style={{ ...styles.btnLight, flex: 1, minWidth: '160px' }}
                type="button"
                onClick={() => setShowQuickAddVendor(false)}
              >
                CANCEL
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ExpensesEntry;